import React, { useContext, useEffect } from 'react';
import '../App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faDownload, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext.js';
import { labs } from '../data/labs.js';

function Item ({ item }) {
    if (typeof item === 'string') return item;
    return <><strong>{item.lead}</strong> {item.text}</>;
}

function Cell ({ value }) {
    if (Array.isArray(value)) {
        return value.map((line, index) => <p key={index}>{line}</p>);
    }
    return <p>{value}</p>;
}

function Block ({ block }) {
    switch (block.type) {
        case 'p':
            return <p>{block.text}</p>;
        case 'note':
            return <p className="report-note">{block.text}</p>;
        case 'ul':
            return <ul>{block.items.map((item, index) => <li key={index}><Item item={item} /></li>)}</ul>;
        case 'ol':
            return <ol>{block.items.map((item, index) => <li key={index}><Item item={item} /></li>)}</ol>;
        case 'code':
            return <pre className="report-code"><code>{block.lines.join('\n')}</code></pre>;
        case 'table':
            return (
                <div className="report-table-wrap">
                    <table className="report-table">
                        <thead>
                            <tr>{block.head.map((cell, index) => <th key={index}>{cell}</th>)}</tr>
                        </thead>
                        <tbody>
                            {block.rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => <td key={cellIndex}><Cell value={cell} /></td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        default:
            return null;
    }
}

function LabReport () {
    const { theme, toggleTheme } = useContext(ThemeContext);
    const { slug } = useParams();
    const lab = labs.find((entry) => entry.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!lab) return undefined;
        const previousTitle = document.title;
        document.title = lab.title + ' — Arturo Nuñez';
        return () => { document.title = previousTitle; };
    }, [lab]);

    return (
        <>
            <div className="block header-contact">
                <Link className="header-monogram" to="/" title="Arturo Nuñez" aria-label="Arturo Nuñez, back to portfolio">AN</Link>
                <div>
                    <button
                        className="theme-toggle"
                        onClick={toggleTheme}
                        title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        aria-label="Toggle theme"
                    >
                        <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} size='lg' />
                    </button>
                </div>
            </div>
            <main className="report-page">
                {lab ? (
                    <article className="report">
                        <a className="report-back" href="/#labs">
                            <FontAwesomeIcon icon={faArrowLeft} /> Security Labs &amp; Write-ups
                        </a>
                        <span className="report-eyebrow">{lab.platform} · {lab.tags}</span>
                        <h1>{lab.reportTitle}</h1>
                        <p className="report-subtitle">{lab.subtitle}</p>
                        <dl className="report-facts">
                            {lab.facts.map(([label, value]) => (
                                <div key={label}>
                                    <dt>{label}</dt>
                                    <dd>{value}</dd>
                                </div>
                            ))}
                        </dl>
                        {lab.sections.map((section) => (
                            <section key={section.heading}>
                                <h2>{section.heading}</h2>
                                {section.blocks.map((block, index) => <Block key={index} block={block} />)}
                            </section>
                        ))}
                        <a className="lab-btn report-pdf" href={lab.pdf} download>
                            <FontAwesomeIcon icon={faDownload} /> Download as PDF
                        </a>
                    </article>
                ) : (
                    <article className="report">
                        <h1>Report not found</h1>
                        <p><a className="report-back" href="/#labs">Back to Security Labs &amp; Write-ups</a></p>
                    </article>
                )}
            </main>
            <div className="report-footer">
                <a href="/">Portfolio Arturonunez.org</a>
                <span>·</span>
                <a href="/#impressum">Impressum</a>
            </div>
        </>
    );
}

export default LabReport;
